import React, {useEffect, useState} from "react";
import {Filter, Loader2, Search, ShieldAlert} from "lucide-react";
import {PaymentHistorySearchRequest, PaymentHistorySearchResponse} from "../../../api/ApiTypes.ts";
import SamButton from "../../../components/SamButton.tsx";
import {useAppraiserApi, useConstantsApi} from "../../../context/ApiContext.tsx";
import {formatDate} from "../../../utils/dateUtils.ts";
import {PaymentsTabProps} from "./Payments.tsx";


export const PaymentHistory: React.FC<PaymentsTabProps> = ({appraiserInfo}) => {

    const [ searchParams, setSearchParams ] = useState<PaymentHistorySearchRequest>({})
    const [loading, setLoading ] = useState<boolean>(false);
    const appraiserApi  = useAppraiserApi();
    const [ payments, setPayments ] = useState<PaymentHistorySearchResponse[]>([])
    const constantsApi = useConstantsApi();
    const [ techFee, setTechFee ] = useState<number>();

    const handleDatePickChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const searchPayments = () => {

        setLoading(true);

        if (!appraiserInfo?.id) {
            console.log("no appraiser for searching")
            return;
        }

        appraiserApi.getPaymentHistory(searchParams, appraiserInfo.id).then(paymentHistoryResponse => {
            setPayments(paymentHistoryResponse)
        }).catch(console.log);

        setLoading(false);
    }

    useEffect(() => {
        searchAmcTechFee();

        searchPayments();
    }, []);

    const searchAmcTechFee = () => {
        constantsApi.getAmcTechFee().then(techFeeResponse => {
            setTechFee(typeof techFeeResponse === 'number' ? techFeeResponse : undefined)
        }).catch(console.log);
    }

    return (
        <>
            <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                    <label className="block text-md font-bold text-black-700 mb-1">
                        Payment Date Range
                    </label>
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            name="createDateFrom"
                            value={ searchParams.createDateFrom ?? '' }
                            onChange={ handleDatePickChange }
                            className="w-40 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <span className="text-gray-500">to</span>
                        <input
                            type="date"
                            name="createDateTo"
                            value={ searchParams.createDateTo ?? '' }
                            min={ searchParams.createDateFrom ?? '' }
                            onChange={ handleDatePickChange }
                            className="w-40 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 mr-3"
                        />
                        <SamButton
                            onClick={ searchPayments }
                        >
                            <div
                                className="flex items-center">
                                <Search className="mr-1.5"/>
                                Search
                            </div>
                        </SamButton>
                    </div>
                </div>
            </div>

            {techFee != null && (
                <div className="flex items-center  space-x-2 mt-2 mb-2 bg-[#edf0f5] p-4 rounded-lg text-sm text-black-900">
                    <ShieldAlert/>
                    <p className="">
                        The figures below do not reflect the ${techFee} Technology Fee as stated in the engagement letter.
                    </p>
                </div>
            )}

            {/* Loading state */}
            {loading && (
                <div className="flex items-center justify-center p-8">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <span className="ml-2 text-gray-600">Loading payments...</span>
                </div>
            )}

            {/* Empty state */}
            {!loading && payments.length === 0 && (
                <div className="text-center py-12 px-4">
                    <div className="text-gray-400 mb-2">
                        <Filter className="h-12 w-12 mx-auto" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">No payments found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        Try adjusting your search filters to find what you're looking for.
                    </p>
                </div>
            )}

            {/* Desktop Table View (hidden on mobile) */}
            {!loading && payments.length > 0 && (
                <div className="hidden md:block border border-gray-100 rounded-lg shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-900 tracking-wider border-b border-gray-100 w-24">
                                    File #
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider border-b border-gray-100 w-28">
                                    Address
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider border-b border-gray-100 w-48">
                                    Payment Amount
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider border-b border-gray-100 w-36">
                                    Payment Date
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider border-b border-gray-100 w-32 hidden lg:table-cell">
                                    Borrower
                                </th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                            {payments.map((payment, index) => (
                                <tr key={payment.usaFileNumber + "-" + index} className="hover:bg-gray-50/50">
                                    <td className="px-3 py-3 text-sm whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium`}>
                                                        {payment.usaFileNumber}
                                                    </span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-3 text-sm whitespace-nowrap">
                                        {payment.address}
                                    </td>
                                    <td className="px-3 py-3 text-sm whitespace-nowrap">
                                        ${payment.paymentAmount}
                                    </td>
                                    <td className="px-3 py-3 text-sm whitespace-nowrap">
                                        <div className="truncate max-w-[150px]">{formatDate(payment.paymentDate)}</div>
                                    </td>
                                    <td className="px-3 py-3 text-sm whitespace-nowrap hidden lg:table-cell">
                                        <div className="truncate max-w-[120px]">{payment.borrower}</div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Mobile Card View (visible only on mobile) */}
            {!loading && payments.length > 0 && (
                <div className="md:hidden space-y-4">
                    {payments.map((payment, index) => (
                        <div key={payment.usaFileNumber + "-" + index} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                            {/* Card Header */}
                            <div className="p-4 flex items-center justify-between border-b border-gray-100">
                                <div className="flex items-center space-x-3">
                                    <div className="text-sm text-gray-500">File #</div>
                                    <div className="font-medium">{payment.usaFileNumber}</div>
                                </div>
                            </div>

                            {/* Card Content */}
                            <div className="p-4 flex flex-col space-y-3">
                                {/* Always visible info */}
                                <div>
                                    <div className="text-sm text-gray-500">Address</div>
                                    <div className="font-medium">{payment.address}</div>
                                </div>

                                <div className="flex justify-between">
                                    <div>
                                        <div className="text-sm text-gray-500">Payment Amount</div>
                                        <div className="font-medium">{payment.paymentAmount}</div>
                                    </div>
                                    <div>
                                        <div className="text-sm text-gray-500">Payment Date</div>
                                        <div className="font-medium">{formatDate(payment.paymentDate)}</div>
                                    </div>
                                </div>

                                <div>
                                    <div className="text-sm text-gray-500">Borrower</div>
                                    <div className="font-medium">{payment.borrower}</div>
                                </div>

                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};